import { ArchiveSegmentDialogContent } from '@openx/components-segment/lib/components/ArchiveSegmentsDialog/ArchiveSegmentDialogContent';
import { RenameSegmentDialogContent } from '@openx/components-segment/lib/components/RenameSegmentDialog/RenameSegmentDialogContent';
import { useSegmentsOperations } from '@openx/components-segment/lib/hooks/useSegmentsOperations';
import { validateSeedsAllowedStatus } from '@openx/components-segment/lib/LalSegmentsForm/validationSchema';
import { useShowLal } from '@openx/components-segment/lib/utils/useShowLal';
import type { RenameSegmentRowActionPayload } from '@openx/components-segment/types';
import { type ListSegment, segmentRowActionType } from '@openx/types/segments';
import { sortMenuButtonItems } from '@openx/utils/lib/tableHelpers';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export const useSegmentRowActions = () => {
  const { t } = useTranslation();
  const { callSegmentAction, setConfirmationDisabled, loadingAction } = useSegmentsOperations();
  const navigate = useNavigate();
  const segmentNameTextFields = useRef<HTMLInputElement | null>(null);
  const showLal = useShowLal();

  const [updatingSegmentId, setUpdatingSegmentId] = useState('');

  const isLoading = useCallback(
    (segmentId?: string) => {
      return !!loadingAction && segmentId === updatingSegmentId;
    },
    [loadingAction, updatingSegmentId]
  );

  const segmentRowActions = useMemo(
    () =>
      sortMenuButtonItems<ListSegment>({
        DELETE: {
          color: 'error',
          danger: true,
          'data-test': 'archive',
          label: t('Archive'),
          loading: segment => isLoading(segment?.id),
          onClick: segment => {
            setUpdatingSegmentId(segment.id ?? '');
            callSegmentAction({
              action: segmentRowActionType.DELETE,
              content: (
                <ArchiveSegmentDialogContent data={[segment]} setConfirmationDisabled={setConfirmationDisabled} />
              ),
              segments: [segment],
            });
          },
        },
        RENAME: {
          'data-test': 'rename',
          label: t('Rename'),
          onClick: segment => {
            setUpdatingSegmentId(segment.id ?? '');
            setConfirmationDisabled(true);
            callSegmentAction<RenameSegmentRowActionPayload>({
              action: segmentRowActionType.RENAME,
              content: (
                <RenameSegmentDialogContent
                  ref={segmentNameTextFields}
                  segment={segment}
                  setConfirmationDisabled={setConfirmationDisabled}
                />
              ),
              preProcessSegments: segments => {
                const segment = segments[0];

                return [{ ...segment, newName: segmentNameTextFields.current?.value }];
              },
              segments: [segment],
            });
          },
        },
        ...(showLal && {
          CREATE_LOOKALIKE: {
            'data-test': 'create-lookalike',
            label: t('Create LAL Segment'),
            allowed: segment => validateSeedsAllowedStatus([segment]),
            notAllowedMessage: t('Segment must be in Ready status to create a LAL Segment'),
            onClick: segment => {
              navigate('/segments/lookalike/create', {
                state: {
                  segments: [segment],
                },
              });
            },
          },
        }),
      }),
    [t, isLoading, callSegmentAction, setConfirmationDisabled, showLal, navigate]
  );

  return segmentRowActions;
};
